/**
 * Community issues — Personal / Community tabs, status pills, optimistic
 * upvote toggle (community only), notify-scope label, FAB → report.
 * Wired: GET /api/issues?visibility=. Port of IssuesScreen in
 * design/source/screens-vendor-issue.jsx.
 */
import { router } from 'expo-router';
import React, { useState } from 'react';
import { Pressable, View } from 'react-native';

import { radius, space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { CardSkeletons, CatPill, Fab, goBack, SubTabs, UpvotePill } from '@/features/community/bits';
import { fetchIssues, issueCatLabel, upvoteIssue, useFetch, withUpvote, type Issue, type IssueVisibility } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, EmptyState, Hairline, Pill, Screen, TopBar } from '@/ui';

export default function IssuesScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [tab, setTab] = useState<IssueVisibility>('COMMUNITY');
  const { data, loading, error, refetch, mutate } = useFetch(() => fetchIssues(tab), [tab]);
  const [upvoteErr, setUpvoteErr] = useState<string | null>(null);

  const list = data ?? [];

  const toggleUpvote = async (issue: Issue) => {
    setUpvoteErr(null);
    mutate((prev) => (prev ?? []).map((i) => (i.id === issue.id ? withUpvote(i) : i)));
    try {
      await upvoteIssue(issue.id);
    } catch {
      mutate((prev) => (prev ?? []).map((i) => (i.id === issue.id ? withUpvote(i) : i)));
      setUpvoteErr(t('issue_upvote_error'));
    }
  };

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('ct_tab_issues')} onBack={goBack} />
      <SubTabs
        tabs={[['PERSONAL', t('issue_tab_personal')], ['COMMUNITY', t('tab_community')]] as const}
        value={tab}
        onChange={setTab}
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {loading ? (
          <CardSkeletons />
        ) : error ? (
          <EmptyState
            icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
            title={t('ct_issues_err_title')}
            body={t('issue_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
            title={tab === 'COMMUNITY' ? t('ct_issues_empty_title') : t('issue_empty_personal_title')}
            body={
              tab === 'COMMUNITY'
                ? t('ct_issues_empty_body')
                : t('issue_empty_personal_body')
            }
          />
        ) : (
          <View>
            {upvoteErr ? <AppText variant="bodySm" tone="danger" style={{ marginBottom: space.s2 }}>{upvoteErr}</AppText> : null}
            {list.map((it, i) => (
              <View key={it.id}>
                <IssueRow issue={it} onUpvote={() => void toggleUpvote(it)} />
                {i < list.length - 1 ? <Hairline /> : null}
              </View>
            ))}
          </View>
        )}
      </Screen>
      <Fab label={t('issue_report')} onPress={() => router.push('/issues/report')} />
    </View>
  );
}

function IssueRow({ issue, onUpvote }: { issue: Issue; onUpvote: () => void }) {
  const { t } = useT();
  const { colors } = useTheme();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/issues/${issue.id}`)}
      style={({ pressed }) => ({ paddingVertical: space.s4, backgroundColor: pressed ? colors.canvas1 : 'transparent', borderRadius: radius.md })}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
          <CatPill label={issueCatLabel(issue.category, t)} />
          <Pill status={issue.status} />
        </View>
        {issue.when ? <AppText variant="bodySm" tone="tertiary">{issue.when}</AppText> : null}
      </View>
      <AppText variant="headingSm" weight="500" style={{ marginTop: space.s2 }}>{issue.title}</AppText>
      {issue.body ? (
        <AppText variant="bodyMd" tone="secondary" numberOfLines={2} style={{ marginTop: 4 }}>{issue.body}</AppText>
      ) : null}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: space.s3 }}>
        {issue.visibility === 'COMMUNITY' ? (
          <UpvotePill count={issue.upvotes} active={issue.upvoted} onPress={onUpvote} />
        ) : (
          <AppText variant="bodySm" tone="tertiary">{issue.notify}</AppText>
        )}
        <AppText variant="bodySm" tone="tertiary">{t('ct_comments', { n: issue.comments })}</AppText>
      </View>
      {issue.visibility === 'COMMUNITY' && issue.notify ? (
        <AppText variant="label" tone="tertiary" style={{ marginTop: space.s2 }}>{issue.notify}</AppText>
      ) : null}
    </Pressable>
  );
}
